"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Mail, MapPin, Phone } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactUs() {
  return <section className="bg-dark-background py-16 px-4 md:py-24">
      <div className="container mx-auto max-w-7xl">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <FormProvider className="w-full" formId="contact-inquiry-form" sectionName="contact-us" defaultValues={{
          name: "",
          email: "",
          company: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 50
          },
          email: {
            required: true,
            pattern: "email"
          },
          company: {
            required: true,
            minLength: 2,
            maxLength: 100
          },
          message: {
            required: true,
            minLength: 10,
            maxLength: 1000
          }
        }} submitText="Send Request" successMessage="Thank you! We'll get back to you soon." errorMessage="Failed to send your request. Please try again.">
            <ContactFormContent />
          </FormProvider>

          <ContactInfo />
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex h-full items-center justify-center">
        <div className="rounded-lg border border-accent/20 bg-accent/10 p-8 text-center">
          <div className="mb-4 inline-flex size-16 items-center justify-center rounded-full bg-accent/20">
            <Mail className="size-8 text-accent" />
          </div>
          <p className="mb-6 text-lg text-foreground-dark">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="border-accent/40 bg-transparent text-accent hover:bg-accent/10 hover:text-accent">
            Send Another Message
          </Button>
        </div>
      </div>;
  }
  return <div className="w-full">
      <div className="mb-8">
        <h2 className="mb-3 font-display text-3xl font-bold text-foreground-dark md:text-4xl">
          Get In Touch
        </h2>
        <p className="text-base text-foreground-dark/70 md:text-lg">
          Fill out the form below and we'll get back to you as soon as possible.
        </p>
      </div>

      <Form onSubmit={handleSubmit}>
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 50,
          message: "Name must not exceed 50 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark">Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" {...field} className="border-muted/30 bg-transparent text-foreground-dark placeholder:text-foreground-dark/40 focus-visible:border-accent focus-visible:ring-accent/20 focus-visible:translate-y-[-4px] transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@example.com" {...field} className="border-muted/30 bg-transparent text-foreground-dark placeholder:text-foreground-dark/40 focus-visible:border-accent focus-visible:ring-accent/20 focus-visible:translate-y-[-4px] transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        required: "Company name is required",
        minLength: {
          value: 2,
          message: "Company name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Company name must not exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark">Company</FormLabel>
              <FormControl>
                <Input placeholder="Your Company" {...field} className="border-muted/30 bg-transparent text-foreground-dark placeholder:text-foreground-dark/40 focus-visible:border-accent focus-visible:ring-accent/20 focus-visible:translate-y-[-4px] transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        },
        maxLength: {
          value: 1000,
          message: "Message must not exceed 1000 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground-dark">Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your inquiry..." {...field} className="min-h-32 border-muted/30 bg-transparent text-foreground-dark placeholder:text-foreground-dark/40 focus-visible:border-accent focus-visible:ring-accent/20 focus-visible:translate-y-[-4px] transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
            <p className="text-sm text-red-400">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="relative w-full overflow-hidden bg-accent text-foreground-accent hover:bg-accent-hover active:bg-accent-active focus-visible:ring-accent/50 disabled:opacity-50 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
          <span className="relative z-10 flex items-center justify-center">
            {isSubmitting ? <>
                <span className="mr-2 inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
                Sending...
              </> : 'Send Request'}
          </span>
          <span className="absolute inset-0 -translate-x-full bg-accent-hover transition-transform duration-[var(--duration-medium)] ease-[var(--ease-spring)] group-hover:translate-x-0"></span>
        </Button>
      </Form>
    </div>;
}
function ContactInfo() {
  const {
    ref: phoneRef,
    isVisible: phoneVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: emailRef,
    isVisible: emailVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: locationRef,
    isVisible: locationVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div className="flex flex-col justify-center space-y-8">
      <div ref={phoneRef} className={`flex items-start gap-4 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${phoneVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border-2 border-primary bg-primary/10">
          <Phone className="size-6 text-primary" />
        </div>
        <div>
          <h3 className="mb-1 font-display text-lg font-semibold text-foreground-dark">Phone</h3>
          <p className="text-base text-foreground-dark/70">310-801-6662</p>
        </div>
      </div>

      <div ref={emailRef} className={`flex items-start gap-4 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-100 ${emailVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border-2 border-primary bg-primary/10">
          <Mail className="size-6 text-primary" />
        </div>
        <div>
          <h3 className="mb-1 font-display text-lg font-semibold text-foreground-dark">Email</h3>
          <p className="text-base text-foreground-dark/70">askus@flexrnd.com</p>
        </div>
      </div>

      <div ref={locationRef} className={`flex items-start gap-4 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-200 ${locationVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}>
        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg border-2 border-primary bg-primary/10">
          <MapPin className="size-6 text-primary" />
        </div>
        <div>
          <h3 className="mb-1 font-display text-lg font-semibold text-foreground-dark">Location</h3>
          <p className="text-base text-foreground-dark/70">2265 East Foothill Blvd<br />Pasadena, CA 91107</p>
        </div>
      </div>
    </div>;
}
export default ContactUs;